import { difference } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { AUTHZED_RELATIONS } from '@/modules/authentication/rolesConstants';
import {
  EmployeeTier,
  ExtendedCreateUserInput,
  UpdateEmployeeInput,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { sortAlpha } from '@/utils/sortUtils';

import { EmployeeFormShape } from './EmployeeModal';
import {
  EmployeeForm_RoleFragment,
  GetEmployeeQuery,
  useGetEmployeeRoleOptionsQuery,
} from './graphql/GetEmployee.generated';

export function dataToForm(
  data: GetEmployeeQuery | undefined
): EmployeeFormShape | null {
  const employee = data?.employees?.edges?.[0]?.node ?? null;
  if (!employee) {
    return null;
  }

  return {
    employeeId: employee.id,
    userId: employee.user.id,
    firstName: employee.user.firstName,
    lastName: employee.user.lastName,
    email: employee.user.email,
    isAdmin: employee.isAdmin || false,
    roleIds: employee.roles?.map((role) => role.id) || [],
    tier: employee.tier,
    teamIds: employee.teams?.map((team) => team.id) || [],
    clientIds: employee.assignedHouseholds?.map((client) => client.id) || [],
    idpId: employee.idpID ?? '',
  };
}

export function useGetEmployeeRoleOptions() {
  const { data, loading, error } = useGetEmployeeRoleOptionsQuery();
  const roles = getNodes(data?.roles) ?? [];
  return { roles, loading, error };
}

/**
 * Determines if a role is considered "view only" based on its capabilities.
 * A role is considered view only if it has only viewing capabilities and no editing or admin capabilities.
 */
function isViewOnlyRole(role: EmployeeForm_RoleFragment): boolean {
  if (!role.capabilities) {
    return false;
  }

  return (
    role.capabilities?.some(
      (capability) =>
        capability.authzedRelationName === AUTHZED_RELATIONS.households.viewer
    ) ?? false
  );
}

export function useFilteredRoleOptions(
  roles: EmployeeForm_RoleFragment[]
): SelectInputOption<string>[] {
  const selectedTier = useWatch({
    name: 'tier',
  });

  const roleOptions: SelectInputOption<string>[] = useMemo(() => {
    return roles
      .filter((role) => {
        // Filter roles based on user tier
        switch (selectedTier) {
          case EmployeeTier.Advanced:
          case EmployeeTier.Basic:
            // Advanced and Basic users: All CUSTOM/ADMINISTRATOR roles (not view only)
            return !isViewOnlyRole(role);

          case EmployeeTier.ViewOnly:
            // Limited users: View only roles
            return isViewOnlyRole(role);

          default:
            // Fallback to showing all roles
            return true;
        }
      })
      .map((role) => ({
        value: role.id,
        display: role.displayName,
      }))
      .sort((a, b) => sortAlpha(a.display, b.display));
  }, [roles, selectedTier]);

  return roleOptions;
}

export function getMutationInput(
  form: EmployeeFormShape,
  originalForm?: EmployeeFormShape | null,
  canViewHouseholds: boolean = true,
  canEditIdpId: boolean = false
): UpdateEmployeeInput {
  const input: UpdateEmployeeInput = {
    isAdmin: form.isAdmin,
    userID: form.userId,
    clearRoles: true,
    addRoleIDs: form.roleIds,
    tier: form.tier,
    user: {
      firstName: form.firstName,
      lastName: form.lastName,
    },
  };

  // Only touch idpID when the field was actually editable (shown) and its value
  // changed. Gating on canEditIdpId ensures a hidden field can never be diffed as
  // a clear, which would otherwise risk wiping a stored IdP ID on an unrelated save.
  // The form is prefilled with the current value, so an emptied field means
  // "clear it" rather than "leave as-is".
  const originalIdpId = originalForm?.idpId ?? '';
  if (canEditIdpId && form.idpId !== originalIdpId) {
    if (form.idpId) {
      input.idpID = form.idpId;
    } else {
      input.clearIdpID = true;
    }
  }

  if (canViewHouseholds) {
    // Calculate team ID differences
    const originalTeamIds = originalForm?.teamIds || [];
    const currentTeamIds = form.teamIds || [];
    const teamsToAdd = difference(currentTeamIds, originalTeamIds);
    const teamsToRemove = difference(originalTeamIds, currentTeamIds);

    if (teamsToAdd.length > 0) {
      input.addTeamIDs = teamsToAdd;
    }
    if (teamsToRemove.length > 0) {
      input.removeTeamIDs = teamsToRemove;
    }

    // Calculate client ID differences
    const originalClientIds = originalForm?.clientIds || [];
    const currentClientIds = form.clientIds || [];
    const clientsToAdd = difference(currentClientIds, originalClientIds);
    const clientsToRemove = difference(originalClientIds, currentClientIds);

    if (clientsToAdd.length > 0) {
      input.addAssignedHouseholdIDs = clientsToAdd;
    }
    if (clientsToRemove.length > 0) {
      input.removeAssignedHouseholdIDs = clientsToRemove;
    }
  }

  return input;
}

export function getCreateInput(
  form: EmployeeFormShape,
  canViewHouseholds: boolean = true,
  canEditIdpId: boolean = false
): ExtendedCreateUserInput {
  return {
    create: {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
    },
    withEmployee: {
      roleIDs: form.roleIds,
      tier: form.tier,
      assignedHouseholdIDs: canViewHouseholds ? form.clientIds : [],
      teamIDs: canViewHouseholds ? form.teamIds : [],
      // Only send an IdP ID when the field is enabled/shown for the tenant, so a
      // hidden field can never set one. Omit when blank so we never send an empty
      // string to WorkOS.
      idpID: canEditIdpId && form.idpId ? form.idpId : undefined,
    },
  };
}
